import React from 'react';
import { Accordian } from 'V2/Shared/Accordian';
import { Spinner } from 'V2/Shared/Spinner';
import { ChevronDown, ChevronUp } from 'V2/Shared/Icons';
import { ErrorRetry } from 'V2/Shared/ErrorRetry';
import { RateType } from 'V2/Types/Rate';
import styles from '../styles.module.css';

interface OrderSummaryProps {
  data: RateType | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  data,
  isLoading,
  error,
  onRetry,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.accordionLoader}>
          <Spinner variant="blue" />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.accordionLoader}>
          <ErrorRetry onRetry={onRetry}>{error}</ErrorRetry>
        </div>
      );
    }

    if (!data?.paymentDetails) return null;

    const selectedId =
      data.paymentDetails.selectedPaymentPlan?.identifier ??
      data.paymentDetails.defaultPaymentPlan?.identifier;
    const plan = data.paymentDetails.paymentPlans?.find(
      (p) => p.identifier === selectedId
    );

    if (!plan) return null;

    const isFullPay =
      plan.paymentFrequencyIdentifier === 'full_pay' ||
      plan.paymentFrequencyIdentifier === 'in_full';

    return (
      <div className={styles.accordionInner}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Total premium</span>
          <span className={styles.summaryValue}>{plan.totalAmount}</span>
        </div>
        {!isFullPay && plan.downPaymentAmount && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Due today</span>
            <span className={styles.summaryValue}>
              {plan.downPaymentAmount}
            </span>
          </div>
        )}
        {!isFullPay && plan.installmentAmount && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              {plan.paymentFrequencyLabel} payments
            </span>
            <span className={styles.summaryValue}>
              {plan.installmentAmount}
            </span>
          </div>
        )}
        {!isFullPay && plan.paymentDaysOfWeek?.length > 0 && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Payments made on</span>
            <span className={styles.summaryValue}>
              {plan.paymentDaysOfWeek.join(', ')}
            </span>
          </div>
        )}
        {!isFullPay &&
          !plan.paymentDaysOfWeek?.length &&
          plan.paymentDaysOfMonth?.length > 0 && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Payments made on</span>
              <span className={styles.summaryValue}>
                {plan.paymentDaysOfMonth.join(', ')}
              </span>
            </div>
          )}
        {!isFullPay &&
          plan.paymentSchedule?.length > 0 &&
          (() => {
            const nextInstallment = plan.paymentSchedule.find(
              (s) => s.type === 'INSTALLMENT'
            );
            return nextInstallment ? (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Next payment due</span>
                <span className={styles.summaryValue}>
                  {nextInstallment.date}
                </span>
              </div>
            ) : null;
          })()}
      </div>
    );
  };

  return (
    <Accordian
      title="Order summary"
      className={styles.accordionCard}
      triggers={{
        expand: <ChevronDown color="#313541" />,
        collapse: <ChevronUp color="#313541" />,
      }}
    >
      {renderContent()}
    </Accordian>
  );
};
